// @ts-nocheck
import React, { useEffect, useState, type CSSProperties } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { useAddressCheckIn } from '../context/AddressCheckInContext';
import { useMixpanel } from '../hooks/useMixpanel';
import { MixpanelEvents } from '../constants/mixpanel-events';
import { ProgressBar } from '../components/ProgressBar';
import { ImageActionSheet } from '../components/ImageActionSheet';
import { CameraModal } from '../components/CameraModal';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { ADDRESS_CHECK_IN_DOC_TYPES } from '../types/enums';
import { ADDRESS_CHECK_IN_STEPS } from '../types/check-in';
import { Routes } from '../types/navigation';
import { useAppNavigate, useRouteParams } from '../navigation/useAppNavigate';
import type { ProofOfAddressImages } from '../types';

const DOC_TYPES = [
  { value: ADDRESS_CHECK_IN_DOC_TYPES.utilityBill, label: 'address_check_in.UtilityBill' },
  { value: ADDRESS_CHECK_IN_DOC_TYPES.payCheck, label: 'address_check_in.Paycheck' },
  { value: ADDRESS_CHECK_IN_DOC_TYPES.financialStatement, label: 'address_check_in.FinancialStatement' },
];

export function AddressCheckInScreen() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { navigateTo, goBack } = useAppNavigate();
  const params = useRouteParams<Routes.AddressCheckIn>();
  const { track } = useMixpanel();
  const addressCtx = useAddressCheckIn();

  const userId = params.userId;
  const address = params.address;

  const [docType, setDocType] = useState<ADDRESS_CHECK_IN_DOC_TYPES>(
    ADDRESS_CHECK_IN_DOC_TYPES.utilityBill,
  );
  const [proofImages, setProofImages] = useState<ProofOfAddressImages[]>([]);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [cameraVisible, setCameraVisible] = useState(false);
  const [loading] = useState(false);
  const [showDocPicker, setShowDocPicker] = useState(false);

  useEffect(() => {
    track(MixpanelEvents.SCREEN_ADDRESS_CHECK_IN, { userId });
    track(MixpanelEvents.ADDRESS_CHECKIN_STARTED, { userId });
  }, []);

  const onDocTypeChange = (type: ADDRESS_CHECK_IN_DOC_TYPES) => {
    setDocType(type);
    setShowDocPicker(false);
    track(MixpanelEvents.ADDRESS_DOC_TYPE_CHANGED, { userId, docType: type });
  };

  const onCameraCapture = (base64: string) => {
    setCameraVisible(false);
    const img: ProofOfAddressImages = {
      url: base64,
      name: `${Date.now()}.jpeg`,
      fileType: 'jpeg',
      type: 'CAMERA' as any,
      isSynced: false,
      isUploading: false,
    };
    setProofImages((prev) => [...prev, img]);
    track(MixpanelEvents.ADDRESS_DOC_PHOTO_CAPTURED, { userId });
  };

  const onFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      const img: ProofOfAddressImages = {
        url: base64,
        name: file.name,
        fileType: file.type.split('/')[1] || 'jpeg',
        type: 'FILE' as any,
        isSynced: false,
        isUploading: false,
      };
      setProofImages((prev) => [...prev, img]);
      track(MixpanelEvents.ADDRESS_DOC_PHOTO_CAPTURED, { userId });
    };
    reader.readAsDataURL(file);
  };

  const continueToImages = () => {
    addressCtx.setEditCheckInData({
      address: address ? {
        formattedAddress: address.formattedAddress,
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2,
        city: address.city,
        state: address.state,
        country: address.country,
        zip: address.zip,
        latitude: address.latitude,
        longitude: address.longitude,
      } : undefined,
      documentType: docType,
      proof: proofImages,
    });
    navigateTo(Routes.AddressCheckInImages, { userId });
  };

  const canContinue = proofImages.length > 0;

  return (
    <div style={{ ...styles.container, backgroundColor: theme.colors.white }}>
      <div style={styles.header}>
        <button style={{ ...styles.backBtn, color: theme.colors.primary }} onClick={goBack}><ArrowLeft size={20} /></button>
        <span style={{ ...styles.title, color: theme.colors.primary }}>
          {t('address_check_in.header')}
        </span>
      </div>

      <ProgressBar steps={ADDRESS_CHECK_IN_STEPS} activeStep={1} marginTop={8} />

      <div style={styles.content}>
        {/* Document type selector */}
        <label style={{ ...styles.label, color: theme.colors.dark }}>
          {t('address_check_in.document_type')}
        </label>
        <button
          style={{ ...styles.picker, borderColor: theme.colors.light, color: theme.colors.dark }}
          onClick={() => setShowDocPicker(!showDocPicker)}
        >
          {DOC_TYPES.find((d) => d.value === docType)?.label
            ? t(DOC_TYPES.find((d) => d.value === docType)!.label)
            : t('address_check_in.select_doc_type')}
        </button>

        {showDocPicker && (
          <div style={{ ...styles.dropDown, backgroundColor: theme.colors.white, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
            {DOC_TYPES.map((doc) => (
              <button
                key={doc.value}
                style={{ ...styles.dropDownItem, color: theme.colors.dark }}
                onClick={() => onDocTypeChange(doc.value)}
              >
                {t(doc.label)}
              </button>
            ))}
          </div>
        )}

        {/* Upload section */}
        <p style={{ ...styles.sectionTitle, color: theme.colors.dark }}>
          {t('address_check_in.upload_proof_of_address')}
        </p>

        <div style={styles.imageGrid}>
          {proofImages.map((img, idx) => (
            <div key={idx} style={styles.imageThumb}>
              <img src={img.url} style={styles.thumbImage} alt={`proof-${idx}`} />
            </div>
          ))}
          <button
            style={{ ...styles.addButton, backgroundColor: '#3880f82b' }}
            onClick={() => setShowActionSheet(true)}
          >
            <span style={{ color: theme.colors.primary, fontSize: 24 }}>+</span>
          </button>
        </div>

        {/* Hidden file input for gallery/file picking */}
        <input
          type="file"
          accept="image/*,application/pdf"
          style={{ display: 'none' }}
          id="ownli-file-input"
          onChange={onFileSelected}
        />
      </div>

      <div style={styles.bottomBar}>
        <button
          style={{
            ...styles.continueButton,
            backgroundColor: canContinue ? theme.colors.primary : theme.colors.light,
            cursor: canContinue ? 'pointer' : 'not-allowed',
          }}
          onClick={continueToImages}
          disabled={!canContinue}
        >
          {t('buttons.continue')}
        </button>
      </div>

      <LoadingOverlay visible={loading} />
      <ImageActionSheet
        visible={showActionSheet}
        onCamera={() => { setShowActionSheet(false); setCameraVisible(true); }}
        onGallery={() => { setShowActionSheet(false); document.getElementById('ownli-file-input')?.click(); }}
        onFiles={() => { setShowActionSheet(false); document.getElementById('ownli-file-input')?.click(); }}
        onDismiss={() => setShowActionSheet(false)}
      />
      <CameraModal
        visible={cameraVisible}
        photo={{ type: 'document', placeholder: '' }}
        onCapture={onCameraCapture}
        onDismiss={() => setCameraVisible(false)}
      />
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', alignItems: 'center', padding: '12px 16px', gap: 12 },
  backBtn: { background: 'none', border: 'none', fontSize: 24, cursor: 'pointer' },
  title: { fontSize: 16, fontWeight: 'bold' },
  content: { flex: 1, overflow: 'auto', padding: '0 16px' },
  label: { display: 'block', fontSize: 14, fontWeight: 'bold', marginTop: 16, marginBottom: 8 },
  picker: { display: 'block', width: '100%', border: '1px solid', borderRadius: 4, padding: '12px', fontSize: 14, textAlign: 'left' as const, background: 'none', cursor: 'pointer', boxSizing: 'border-box' as const },
  dropDown: { borderRadius: 4, marginTop: 4, padding: 8 },
  dropDownItem: { display: 'block', width: '100%', padding: '10px 0', borderBottom: '1px solid #e0e0e0', background: 'none', border: 'none', borderBottomWidth: 1, borderBottomStyle: 'solid' as const, borderBottomColor: '#e0e0e0', fontSize: 13, textAlign: 'left' as const, cursor: 'pointer' },
  sectionTitle: { fontSize: 14, fontWeight: 'bold', marginTop: 20, marginBottom: 12 },
  imageGrid: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  imageThumb: { width: 80, height: 80, borderRadius: 4, overflow: 'hidden' },
  thumbImage: { width: '100%', height: '100%', objectFit: 'cover' as const },
  addButton: { width: 80, height: 80, borderRadius: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', border: 'none', cursor: 'pointer' },
  bottomBar: { padding: 16 },
  continueButton: { width: '100%', padding: '14px 0', borderRadius: 4, border: 'none', color: '#fff', fontSize: 16, fontWeight: 'bold', textAlign: 'center' as const },
};
